import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Users, ArrowRight, AlertCircle } from "lucide-react";

export default function JoinGroup() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [group, setGroup] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/groups/by-code/${code}`);
        setGroup(data);
      } catch (e) {
        setError(e?.response?.data?.detail || "Invalid invite link");
      }
    })();
  }, [code]);

  const join = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/groups/join`, { code });
      toast.success(`Joined ${data.name}!`);
      navigate(`/groups/${data.group_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't join");
    } finally { setBusy(false); }
  };

  return (
    <div className="px-3 md:px-8 py-12 grid-bg min-h-[calc(100vh-65px)] flex items-start justify-center" data-testid="join-group-page">
      <div className="surface-card p-8 max-w-md w-full text-center">
        {error ? (
          <>
            <AlertCircle size={36} className="mx-auto text-primary-accent mb-3" />
            <h1 className="font-display text-2xl uppercase mb-2">Invalid Link</h1>
            <p className="text-sm text-zinc-400 mb-5" data-testid="join-error">{error}</p>
            <Link to="/dashboard" className="text-xs uppercase tracking-widest text-primary-accent">Back to Dashboard</Link>
          </>
        ) : group ? (
          <>
            <div className="label-tag mb-2">You have been invited to join</div>
            <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center" style={{ background: group.color || "#FF3B30" }}>
              <Users size={26} className="text-white" />
            </div>
            <h1 className="font-display text-3xl uppercase mb-1" data-testid="join-group-name">{group.name}</h1>
            {group.description && <p className="text-sm text-zinc-400 mb-2">{group.description}</p>}
            <div className="text-xs uppercase tracking-widest text-zinc-500 mb-6">{group.members} member{group.members === 1 ? "" : "s"}</div>
            {group.status === "archived" ? (
              <div className="w-full border border-primary-accent/40 text-primary-accent px-5 py-3 text-sm uppercase tracking-widest" data-testid="join-archived-note">
                This group is archived and can't be joined
              </div>
            ) : (
              <button data-testid="confirm-join-btn" onClick={join} disabled={busy}
                className="w-full bg-primary-accent text-white px-5 py-3 font-display tracking-widest text-sm uppercase flex items-center justify-center gap-2 hover:bg-[var(--primary-hover)] disabled:opacity-50">
                {busy ? "Joining…" : <>Join Group <ArrowRight size={16} /></>}
              </button>
            )}
          </>
        ) : (
          <div className="text-zinc-500 py-8">Loading invite…</div>
        )}
      </div>
    </div>
  );
}
